import { readRoster, readSettingMode, isFixedGameSetting } from '@repo/sdk'

import {
  expandWolfSiblingChoice,
  isWolfSiblingSeatKey,
  WOLF_SIBLING_PAIR_KEY,
} from './wolf-sibling-configuration'

const { humanVsAiDisabledRoles } = readRoster({ kind: 'catalog' }).choices

function isHumanVsAiModelRoleAllowed(role: string): boolean {
  return !(humanVsAiDisabledRoles as readonly string[]).includes(role)
}

export type RoleModelAssignments = Record<string, string>

export function getResponsiveModelPoolSignature(
  responsiveModels: readonly string[],
): string {
  return [...new Set(responsiveModels.filter(Boolean))].sort().join('\u0000')
}

export function getRoleModelRerollContext(
  gameSetting: string,
  responsivePoolSignature: string,
  isSupported: boolean,
): string {
  if (!isSupported || !responsivePoolSignature)
    return ''
  return `${(readSettingMode(gameSetting).symbolicPlayerCount !== null) ? 'BRAWL' : gameSetting}\u0000${responsivePoolSignature}`
}

export interface ModelSeatDescriptor {
  sourceKey: string
  runtimeKey: string
}

interface PlatformSeatKeyOptions {
  gameSetting: string
  allGoodRoleKeys: readonly string[]
  allEvilRoleKeys: readonly string[]
  fixedVillagerCount: number
  selectedRoleKeys: readonly string[]
  selectedVillagerIndices: readonly number[]
}

function fixedSeat(sourceKey: string): ModelSeatDescriptor {
  return { sourceKey, runtimeKey: sourceKey }
}

function runtimeWerewolfKey(index: number): string {
  return index === 0 ? 'werewolf' : `werewolf_${index + 1}`
}

export function buildModelSeatDescriptors({
  gameSetting,
  allGoodRoleKeys,
  allEvilRoleKeys,
  fixedVillagerCount,
  selectedRoleKeys,
  selectedVillagerIndices,
}: PlatformSeatKeyOptions): ModelSeatDescriptor[] {
  if ((readSettingMode(gameSetting).symbolicPlayerCount !== null)) {
    return [
      ...allGoodRoleKeys.map(sourceKey => fixedSeat(sourceKey)),
      ...Array.from({ length: 4 }, (_, index) =>
        fixedSeat(`villager_${index}`)),
      ...allEvilRoleKeys.flatMap(roleKey => expandWolfSiblingChoice(roleKey))
        .map(sourceKey => fixedSeat(sourceKey)),
    ]
  }

  if (
    gameSetting === 'G6_1SR1WT_2WW_2VG'
    || gameSetting === 'G9_1HT1SR1WT_3WW_3VG'
    || isFixedGameSetting(gameSetting)
  ) {
    return [
      ...allGoodRoleKeys.map(sourceKey => fixedSeat(sourceKey)),
      ...Array.from({ length: fixedVillagerCount }, (_, index) =>
        fixedSeat(`villager_${index}`)),
      ...allEvilRoleKeys.map(sourceKey => fixedSeat(sourceKey)),
    ]
  }

  const selectedRoleSet = new Set(selectedRoleKeys)
  const goodSeats = allGoodRoleKeys
    .filter(sourceKey => selectedRoleSet.has(sourceKey))
    .map(sourceKey => fixedSeat(sourceKey))
  let werewolfIndex = 0
  const evilSeats = allEvilRoleKeys.flatMap(roleKey => expandWolfSiblingChoice(roleKey))
    .filter(sourceKey => gameSetting !== 'HUMAN_VS_AI'
      || isHumanVsAiModelRoleAllowed(sourceKey))
    .filter(sourceKey => selectedRoleSet.has(sourceKey)
      || (isWolfSiblingSeatKey(sourceKey)
        && selectedRoleSet.has(WOLF_SIBLING_PAIR_KEY)))
    .map((sourceKey) => {
      if (sourceKey === 'werewolf' || /^werewolf_\d+$/.test(sourceKey)) {
        const descriptor = {
          sourceKey,
          runtimeKey: runtimeWerewolfKey(werewolfIndex),
        }
        werewolfIndex += 1
        return descriptor
      }
      return fixedSeat(sourceKey)
    })
  const villagerSeats = [...new Set(selectedVillagerIndices)]
    .sort((left, right) => left - right)
    .map((sourceIndex, runtimeIndex) => ({
      sourceKey: `villager_${sourceIndex}`,
      runtimeKey: `villager_${runtimeIndex}`,
    }))

  return [...goodSeats, ...villagerSeats, ...evilSeats]
}

export function buildPlatformModelSeatKeys(
  options: PlatformSeatKeyOptions,
): string[] {
  return buildModelSeatDescriptors(options).map(seat => seat.runtimeKey)
}

export function normalizeSeatAssignments(
  assignments: Readonly<Record<string, string>>,
  seats: readonly ModelSeatDescriptor[],
): RoleModelAssignments {
  const normalized: RoleModelAssignments = {}
  for (const { sourceKey, runtimeKey } of seats) {
    const model = assignments[sourceKey]
    if (model)
      normalized[runtimeKey] = model
  }
  return normalized
}

export function splitByomSeatAssignments(
  normalizedAssignments: Readonly<RoleModelAssignments>,
): {
  byomAssignments: RoleModelAssignments
  officialModelAssignments: RoleModelAssignments
} {
  const byomAssignments: RoleModelAssignments = {}
  const officialModelAssignments: RoleModelAssignments = {}
  for (const [runtimeKey, value] of Object.entries(normalizedAssignments)) {
    if (!value)
      continue
    if (value === 'custom')
      byomAssignments[runtimeKey] = value
    else officialModelAssignments[runtimeKey] = value
  }
  return { byomAssignments, officialModelAssignments }
}

interface ExplicitHumanSeatOptions {
  seatKey: string
  gameSetting: string
  selectedRole: string
  isMultiplayer: boolean
  evilSeatKeys: readonly string[]
}

export function isExplicitHumanModelSeat({
  seatKey,
  gameSetting,
  selectedRole,
  isMultiplayer,
  evilSeatKeys,
}: ExplicitHumanSeatOptions): boolean {
  if (
    isMultiplayer
    || (readSettingMode(gameSetting).symbolicPlayerCount !== null)
    || gameSetting === 'WATCH_MODE'
  ) {
    return false
  }

  if (gameSetting === 'HUMAN_VS_AI') {
    return isHumanVsAiModelRoleAllowed(seatKey) && evilSeatKeys.includes(seatKey)
  }

  const canonicalRole = seatKey.startsWith('villager_')
    ? 'villager'
    : /^werewolf_\d+$/.test(seatKey)
      ? 'werewolf'
      : seatKey
  if (canonicalRole !== selectedRole)
    return false
  if (seatKey.startsWith('villager_'))
    return seatKey === 'villager_0'
  if (/^werewolf_\d+$/.test(seatKey))
    return false
  return true
}

export function reconcileRoleModelAssignments(
  seatKeys: readonly string[],
  responsiveModels: readonly string[],
  existing: RoleModelAssignments,
  random: () => number = Math.random,
): RoleModelAssignments {
  const pool = [...new Set(responsiveModels.filter(Boolean))]
  if (pool.length === 0)
    return {}

  const next: RoleModelAssignments = {}
  for (const seatKey of new Set(seatKeys)) {
    const existingModel = existing[seatKey]
    if (existingModel && pool.includes(existingModel)) {
      next[seatKey] = existingModel
      continue
    }

    const sampledIndex = Math.min(
      pool.length - 1,
      Math.max(0, Math.floor(random() * pool.length)),
    )
    next[seatKey] = pool[sampledIndex]!
  }

  return next
}

export function sameRoleModelAssignments(
  left: RoleModelAssignments,
  right: RoleModelAssignments,
): boolean {
  const leftEntries = Object.entries(left)
  const rightEntries = Object.entries(right)
  return (
    leftEntries.length === rightEntries.length
    && leftEntries.every(([key, value]) => right[key] === value)
  )
}

export function sampleComparisonModels(
  responsiveModels: readonly string[],
  random: () => number = Math.random,
): [string, string] {
  const pool = [...new Set(responsiveModels.filter(Boolean))]
  if (pool.length === 0)
    return ['', '']

  const firstIndex = Math.min(
    pool.length - 1,
    Math.max(0, Math.floor(random() * pool.length)),
  )
  const first = pool[firstIndex]!
  if (pool.length === 1)
    return [first, first]

  const secondPool = pool.filter(model => model !== first)
  const secondIndex = Math.min(
    secondPool.length - 1,
    Math.max(0, Math.floor(random() * secondPool.length)),
  )
  return [first, secondPool[secondIndex]!]
}
